/** VISTA TÉCNICA SOLUARQ (Fase 5 — Etapa 3)
 *  Vista limpia de presentación derivada del MISMO modelo técnico del Modulador:
 *  alzado con cotas por módulo + total, etiquetas, herrajes con leyenda y planta con ángulos reales. */
import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { ZoomIn, ZoomOut, Maximize } from 'lucide-react';
import { derivarModeloTecnico, PAD } from './simbolos/modeloTecnico';
import { resolverSimbolo, renderSimbolo, SIMBOLOS } from './simbolos/biblioteca';

const NAVY = '#0F172A', AZUL = '#0284C7', GRISL = '#94A3B8', AMBAR = '#B45309';

const Cota = ({ x1, x2, y, texto, bold }) => (
  <g>
    <line x1={x1} y1={y} x2={x2} y2={y} stroke={NAVY} strokeWidth={3} />
    <line x1={x1} y1={y - 14} x2={x1} y2={y + 14} stroke={NAVY} strokeWidth={3} />
    <line x1={x2} y1={y - 14} x2={x2} y2={y + 14} stroke={NAVY} strokeWidth={3} />
    <text x={(x1 + x2) / 2} y={y + 52} textAnchor="middle" fontSize={bold ? 56 : 48}
      fill={bold ? AZUL : NAVY} fontWeight={bold ? 800 : 600}>{texto}</text>
  </g>
);

export const VistaTecnica = ({ familia, receta, codigo }) => {
  const [zoom, setZoom] = useState(1);
  const modelo = useMemo(() => derivarModeloTecnico(receta, familia), [receta, familia]);
  const { modulos, uniones, herrajes, totalW, totalH, planta, tieneGiro, conjunto } = modelo;
  const r = receta || {};
  const vw = totalW + PAD * 2 + 80, vh = totalH + PAD * 2 + 130;
  const cotY = PAD + totalH + 60;
  const barras = modulos.filter((m) => m.barra);
  const VBX = -70;
  return (
    <div className="space-y-2 min-w-0" data-testid="vista-tecnica">
      <div className="flex items-center justify-between flex-wrap gap-1.5">
        <p className="text-[10px] font-black uppercase text-[var(--text-muted)]">
          Vista técnica{codigo ? ` — ${codigo}` : ''}{conjunto ? ` · Conjunto: ${conjunto}` : ''}
        </p>
        <div className="flex gap-1 items-center">
          <span className="text-[10px] text-[var(--text-muted)] mr-1" data-testid="vt-escala">escala {Math.round(zoom * 100)}%</span>
          <Button type="button" variant="ghost" size="sm" className="h-8 px-2" onClick={() => setZoom(Math.min(3, zoom + 0.25))} data-testid="vt-zoom-in"><ZoomIn size={13} /></Button>
          <Button type="button" variant="ghost" size="sm" className="h-8 px-2" onClick={() => setZoom(Math.max(0.4, zoom - 0.25))} data-testid="vt-zoom-out"><ZoomOut size={13} /></Button>
          <Button type="button" variant="ghost" size="sm" className="h-8 px-2" title="Ajustar / centrar" onClick={() => setZoom(1)} data-testid="vt-fit"><Maximize size={13} /></Button>
        </div>
      </div>
      <div className="overflow-auto border border-[var(--border)] rounded-md bg-white" style={{ maxHeight: 420 }}>
        <svg viewBox={`${VBX} 0 ${vw - VBX} ${vh}`} className="max-md:max-w-full" style={{ height: 360 * zoom, width: 'auto', display: 'block', margin: '0 auto' }} data-testid="vt-canvas">
          {/* muro (solo cuando la puerta va embisagrada a muro) */}
          {familia === 'pabm' && (r.embisagrado || 'muro') === 'muro' && (
            <rect x={PAD - 34} y={PAD} width={30} height={totalH} fill="#CBD5E1" stroke={NAVY} strokeWidth={3} />
          )}
          {modulos.map((m) => {
            const esPuerta = m.tipo === 'PABM';
            const bisIzq = esPuerta && m.apertura !== 'izquierda';
            const bx = bisIzq ? m.x + 20 : m.x + m.ancho_mm - 20;
            const jx = bisIzq ? m.x + m.ancho_mm - 40 : m.x + 40;
            const jal = (r.jalador || '') !== 'no lleva' && r.jalador;
            return (
              <g key={m.id} data-testid={`vt-modulo-${m.id}`}>
                <rect x={m.x} y={m.y} width={m.ancho_mm} height={m.alto_mm}
                  fill={esPuerta ? '#F0F9FF' : '#FFFFFF'} stroke={NAVY} strokeWidth={7} />
                {esPuerta ? (<>
                  <path d={`M ${bx} ${m.y} L ${bisIzq ? m.x + m.ancho_mm : m.x} ${m.y + m.alto_mm / 2} L ${bx} ${m.y + m.alto_mm}`}
                    fill="none" stroke={AZUL} strokeWidth={4} strokeDasharray="28 20" />
                  {Array.from({ length: m.nBis }).map((_, i) => (
                    <g key={i} transform={`translate(${bx} ${m.y + ((i + 1) * m.alto_mm) / (m.nBis + 1)})`}>
                      {renderSimbolo((r.embisagrado || 'muro') === 'muro' ? 'bisagra_muro_vidrio' : 'bisagra_vidrio_vidrio_90', NAVY)}
                    </g>
                  ))}
                  {jal && <g transform={`translate(${jx} ${m.y + m.alto_mm / 2})`}>{renderSimbolo(resolverSimbolo(r.jalador), NAVY)}</g>}
                </>) : (<>
                  <line x1={m.x + 26} y1={m.y + 26} x2={m.x + m.ancho_mm - 26} y2={m.y + m.alto_mm - 26} stroke={GRISL} strokeWidth={2.5} />
                  <line x1={m.x + m.ancho_mm - 26} y1={m.y + 26} x2={m.x + 26} y2={m.y + m.alto_mm - 26} stroke={GRISL} strokeWidth={2.5} />
                </>)}
                <text x={m.x + m.ancho_mm / 2} y={m.y + 78} textAnchor="middle" fontSize={54} fill={NAVY} fontWeight={800} data-testid={`vt-etiqueta-${m.etiqueta}`}>{m.etiqueta}</text>
                {m.barra && (<>
                  <rect x={m.x - 20} y={m.y + 96} width={m.ancho_mm + 40} height={16} rx={8} fill={AMBAR} />
                  <text x={m.x + m.ancho_mm / 2} y={m.y + 160} textAnchor="middle" fontSize={36} fill={AMBAR} fontWeight={700}>BARRA ESTABILIZADORA</text>
                </>)}
                <Cota x1={m.x} x2={m.x + m.ancho_mm} y={cotY} texto={`${m.ancho_mm}`} />
              </g>
            );
          })}
          {/* uniones: rombo + U{n} + ángulo + etiqueta técnica (BISAGRA/HERRAJE V-V n° · HERRAJE FIJO-CANCEL VIDRIO-MURO) */}
          {uniones.map((u) => (
            <g key={u._i} data-testid={`vt-union-${u._i}`}>
              <rect x={u.punto.x - 20} y={u.punto.y - 20} width={40} height={40} transform={`rotate(45 ${u.punto.x} ${u.punto.y})`}
                fill="#FDE68A" stroke={AMBAR} strokeWidth={5} />
              <text x={u.punto.x} y={u.punto.y + 10} textAnchor="middle" fontSize={26} fill="#78350F" fontWeight={800}>{u.etiqueta}</text>
              {u.angulo_norm && <text x={u.punto.x} y={u.punto.y - 42} textAnchor="middle" fontSize={44} fill={AMBAR} fontWeight={800}>{u.angulo_norm}°</text>}
              {u.es_muro ? (
                <text x={u.lado_muro === 'der' ? u.punto.x - 34 : u.punto.x + 34} y={u.punto.y + 84}
                  textAnchor={u.lado_muro === 'der' ? 'end' : 'start'} fontSize={30} fill={AMBAR} fontWeight={700}>{u.etiqueta_tecnica}</text>
              ) : (
                <text x={u.punto.x} y={PAD - 26 - (u._i % 2) * 44} textAnchor="middle" fontSize={34} fill={AMBAR} fontWeight={700}>{u.etiqueta_tecnica}</text>
              )}
            </g>
          ))}
          {/* herrajes visuales: mismo registro herrajes_visuales, con etiqueta Hn */}
          {herrajes.map((h) => {
            const sid = resolverSimbolo(h.nombre);
            return (
              <g key={h.hid} transform={`translate(${h.x} ${h.y})`} data-testid={`vt-herraje-${h.etiqueta}`}>
                <g transform={h.orientacion === 'horizontal' ? 'rotate(90)' : undefined}>{renderSimbolo(sid, AMBAR)}</g>
                <text x={0} y={-92} textAnchor="middle" fontSize={44} fill={AMBAR} fontWeight={800}>{h.etiqueta}</text>
              </g>
            );
          })}
          <Cota x1={PAD} x2={PAD + totalW} y={cotY + 96} texto={`TOTAL ${totalW} mm`} bold />
          {/* alto total (izquierda) */}
          <line x1={PAD - 70} y1={PAD} x2={PAD - 70} y2={PAD + totalH} stroke={NAVY} strokeWidth={3} />
          <line x1={PAD - 84} y1={PAD} x2={PAD - 56} y2={PAD} stroke={NAVY} strokeWidth={3} />
          <line x1={PAD - 84} y1={PAD + totalH} x2={PAD - 56} y2={PAD + totalH} stroke={NAVY} strokeWidth={3} />
          <text x={PAD - 96} y={PAD + totalH / 2} textAnchor="middle" fontSize={56} fill={NAVY} fontWeight={700}
            transform={`rotate(-90 ${PAD - 96} ${PAD + totalH / 2})`} data-testid="vt-cota-alto">{totalH} mm</text>
        </svg>
      </div>
      {/* PLANTA con giros reales cuando hay ángulos 90°/135° */}
      {tieneGiro && (() => {
        const segs = planta.filter((s) => s.tipo === 'seg');
        const giros = planta.filter((s) => s.tipo === 'giro');
        const xs = segs.flatMap((s) => [s.x1, s.x2]), ys = segs.flatMap((s) => [s.y1, s.y2]);
        const mx = Math.min(...xs) - 320, Mx = Math.max(...xs) + 320, my = Math.min(...ys) - 300, My = Math.max(...ys) + 300;
        return (
          <div className="overflow-auto border border-[var(--border)] rounded-md bg-white" data-testid="vt-planta">
            <svg viewBox={`${mx} ${my} ${Mx - mx} ${My - my}`} className="max-md:max-w-full" style={{ height: 230, width: 'auto', display: 'block', margin: '0 auto' }}>
              <text x={mx + 30} y={my + 80} fontSize={64} fill={GRISL} fontWeight={800}>PLANTA</text>
              {segs.map((s, i) => {
                const dx = s.x2 - s.x1, dy = s.y2 - s.y1, L = Math.hypot(dx, dy) || 1;
                const nx = (-dy / L) * 60, ny = (dx / L) * 60;
                return (
                  <g key={i}>
                    <line x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2} stroke={s.esPuerta ? AZUL : NAVY} strokeWidth={22} strokeLinecap="square" />
                    <text x={(s.x1 + s.x2) / 2 + nx * 1.6} y={(s.y1 + s.y2) / 2 + ny * 1.6} textAnchor="middle" fontSize={52} fill={NAVY} fontWeight={700}>{s.etiqueta} · {s.largo}</text>
                  </g>
                );
              })}
              {giros.map((g, i) => (
                <g key={i}>
                  <circle cx={g.x} cy={g.y} r={20} fill={AMBAR} />
                  <text x={g.x} y={g.y - 56} textAnchor="middle" fontSize={58} fill={AMBAR} fontWeight={800}>{g.angulo}°</text>
                  {g.etiqueta && <text x={g.x} y={g.y + 110} textAnchor="middle" fontSize={44} fill={AMBAR} fontWeight={700}>{g.etiqueta}</text>}
                </g>
              ))}
            </svg>
          </div>
        );
      })()}
      {/* leyenda técnica compacta (sin precios) — códigos = modelo real, sin duplicar */}
      {modulos.length > 0 && (
        <div className="border border-[var(--border)] rounded-md p-2 grid grid-cols-1 sm:grid-cols-2 gap-1" data-testid="vt-leyenda">
          {modulos.map((m) => (
            <p key={m.id} className="text-[10px] text-[var(--text)]">
              <b className="text-[#0284C7]">{m.etiqueta}</b> — {m.tipo === 'PABM' ? 'Puerta abatible' : 'Vidrio fijo'}{m.tipo === 'PABM' ? ` · Bisagra ${(r.embisagrado || 'muro') === 'muro' ? 'muro-vidrio' : 'vidrio-vidrio'} ×${m.nBis}` : ''}{m.barra ? ' · Barra estabilizadora' : ''}
            </p>
          ))}
          {familia === 'pabm' && r.jalador && r.jalador !== 'no lleva' && (
            <p className="text-[10px] text-[var(--text)]"><b className="text-[#0284C7]">J1</b> — {r.jalador}</p>
          )}
          {herrajes.map((h) => (
            <p key={h.hid} className="text-[10px] text-[var(--text)]">
              <b className="text-amber-600">{h.etiqueta}</b> — {SIMBOLOS[resolverSimbolo(h.nombre)]?.categoria === 'generico' ? `OTRO HERRAJE · ${h.nombre}` : h.nombre}
              {Number(h.cantidad) > 0 ? ` ×${Number(h.cantidad)}` : ''}{h.acabado ? ` · ${h.acabado}` : ''}
            </p>
          ))}
          {uniones.map((u) => (
            <p key={`u${u._i}`} className="text-[10px] text-[var(--text)]">
              <b className="text-amber-600">U{u._i + 1}</b> — {u.etiqueta_tecnica}{(u.cantidad || u.cantidad_sugerida) ? ` ×${u.cantidad || u.cantidad_sugerida}` : ' · cantidad pendiente'}{!u.es_muro && <> · holgura {u.holgura_utilizada !== '' && u.holgura_utilizada != null ? u.holgura_utilizada : (u.holgura_estandar ?? 5)} mm</>}
            </p>
          ))}
        </div>
      )}
      <p className="text-[10px] text-[var(--text-muted)]" data-testid="vt-specs">
        {[r.vidrio?.tipo && `Vidrio ${r.vidrio.tipo} ${r.vidrio.espesor || ''}`.trim(), r.acabado && `Acabado ${r.acabado}`,
          familia === 'pabm' && `Apertura ${r.apertura || 'derecha'}`, familia === 'pabm' && `Embisagrado ${(r.embisagrado || 'muro') === 'muro' ? 'a muro' : 'a fijo'}`,
          barras.length > 0 && `${barras.length} barra(s) estabilizadora(s)`].filter(Boolean).join(' · ') || 'Dibujo proporcional — cotas en mm'}
      </p>
    </div>
  );
};
